import { 
    Box, Card, CardContent, Grid, InputAdornment, Paper, Table, 
    TableBody, TableCell, TableContainer, TableHead, TableRow, 
    TextField, Typography, useMediaQuery, useTheme, Chip 
} from "@mui/material";
import { StyledTableCell, StyledTableRow } from "Pages/AdminList/ProfessionalUserList";
import { useEffect, useState, useMemo } from "react";
import SearchIcon from '@mui/icons-material/Search';
import WalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StarIcon from '@mui/icons-material/Stars';
import HistoryIcon from '@mui/icons-material/History';
import ColorData from "Components/CstmTheme/ColorData";
import PaymentApi from "Service/Payment.api";
import { useDispatch, useSelector } from "react-redux";
import { startLoading, stopLoading } from "../../Redux/Screen/Action";

interface RewardHistoryInterface {
    date: string;
    desc: string;
    amount: number;
    points: number;
    caseTypeName: string;
    orderTypeName: string;
    caseId: number;
    paymentDate: string | null;
}

const RewardsHistory: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useDispatch();
    
    const [searchQuery, setSearchQuery] = useState("");
    const [rewards, setRewards] = useState<RewardHistoryInterface[]>([]);
    
    // Get user info from Redux
    const { myInfo } = useSelector((state: any) => state?.user);

    useEffect(() => {
        fetchRewards();
    }, [myInfo?.id]);

    const fetchRewards = async () => {
        if (!myInfo?.id) return;
        
        dispatch(startLoading({ loading: true, loadtext: "Fetching rewards..." }));
        try {
            const response = await PaymentApi.getRewardHistory(myInfo.id);
            console.log("Reward History Response:", response);
            if (response.success) {
                const data = response.data;
                console.log("Parsed Reward Data:", data);
                setRewards(data || []);
            }
        } catch (error) {
            console.error("Failed to fetch rewards:", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    // --- Calculations for Summary Cards ---
    const summary = useMemo(() => {
        return rewards.reduce((acc, curr) => {
            if (curr.amount > 0) {
            acc.totalSpend += curr.amount;
        }
            acc.totalPoints += curr.points;
            return acc;
        }, { totalSpend: 0, totalPoints: 0 });
    }, [rewards]);

    // --- Filtering Logic ---
    const filteredRewards = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return rewards;
        return rewards.filter(r => 
            r.desc.toLowerCase().includes(query) ||
            r.caseTypeName.toLowerCase().includes(query) ||
            r.amount.toString().includes(query) ||
            r.caseId.toString().includes(query)
        );
    }, [searchQuery, rewards]);

    const renderSummaryCards = () => (
        <Grid container spacing={3} mb={4}>
            <Grid sx={{ xs: 12, sm: 6 }}>
                <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: '#f0f4ff' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: ColorData.primary.main, color: 'white', mr: 2 }}>
                            <WalletIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.primary">Net Lifetime Spend</Typography>
                            <Typography variant="h5" fontWeight="bold">₹{summary.totalSpend.toLocaleString()}</Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            <Grid sx={{ xs: 12, sm: 6 }}>
                <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: '#e6e6ffff', border: '1px solid #7f71faff' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#7bce7f', color: '#333', mr: 2 }}>
                            <StarIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.primary">Current Reward Points</Typography>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#7bce7f' }}>{summary.totalPoints}</Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderTable = () => (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <StyledTableCell>Date</StyledTableCell>
                        <StyledTableCell>Case ID</StyledTableCell>
                        <StyledTableCell>Case Type</StyledTableCell>
                        <StyledTableCell>Order Type</StyledTableCell>
                        <StyledTableCell>Description</StyledTableCell>
                        <StyledTableCell>Amount</StyledTableCell>
                        <StyledTableCell>Points</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredRewards.length === 0 ? (
                        <TableRow><TableCell colSpan={7} align="center">No reward history found</TableCell></TableRow>
                    ) : (
                        filteredRewards.map((row, index) => (
                            <StyledTableRow key={index}>
                                <TableCell>{row.date}</TableCell>
                                 <TableCell>{row.caseId}</TableCell>
                                <TableCell>{row.caseTypeName}</TableCell>
                                <TableCell>{row.orderTypeName}</TableCell>

                                <TableCell>
                                    <Typography variant="body2" fontWeight="medium">{row.desc}</Typography>
                                    <Typography variant="caption" color="textSecondary">{row.orderTypeName}</Typography>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: row.amount < 0 ? 'error.main' : 'text.primary' }}>
                                    ₹{row.amount.toLocaleString()}
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={row.points > 0 ? `+${row.points}` : row.points} 
                                        color={row.points > 0 ? "success" : "error"}
                                        size="small"
                                        sx={{ fontWeight: 'bold', minWidth: 50 }}
                                    />
                                </TableCell>
                               
                            </StyledTableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderCards = () => (
        <Box display="flex" flexDirection="column" gap={2}>
            {filteredRewards.map((row, index) => (
                <Card key={index} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="caption" color="textSecondary">{row.date}</Typography>
                            <Chip label={`${row.points} pts`} color={row.points > 0 ? "success" : "error"} size="small" />
                        </Box>
                        <Typography variant="body2" fontWeight="bold">{row.desc}</Typography>
                        <Typography variant="body2">Amount: ₹{row.amount}</Typography>
                        <Typography variant="caption" display="block">Case ID: {row.caseId}</Typography>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );

    return (
        <Box p={3} width="100%">
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
                padding: 2, borderRadius: 2,
                backgroundColor: ColorData.primary.main,
                color: ColorData.text.secondary,
                display: 'flex', alignItems: 'center', gap: 1
            }}>
                <HistoryIcon /> Reward & Loyalty History
            </Typography>

            {renderSummaryCards()}

            <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search rewards history..."
                size="small"
                fullWidth={isMobile}
                sx={{ mb: 3, width: isMobile ? '100%' : '350px' }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            {isMobile ? renderCards() : renderTable()}
        </Box>
    );
};

export default RewardsHistory;